import {
  Box,
  useTheme,
  Divider,
  Typography,
  Button,
  IconButton,
  Chip,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useStateContext } from "contexts/ContextProvider";
import { useWebSocket } from "contexts/WebSocketContext";
import { useAuth } from "contexts/AuthContext";
import React, { useState, useEffect } from "react";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import Overlay from "components/Overlay/Overlay";

const SERVICE_TAG_REGEX = /#(\d+)/g;

const getServiceRoutePath = (service) => {
  if (!service?.id) return null;
  if (service.ServiceCategory === "Translation") return `/translations/${service.id}`;
  if (service.ServiceCategory === "Interpretation") return `/interpretations/${service.id}`;
  return `/otherServices/${service.id}`;
};

const TaskProfile = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const {
    overlayData,
    setOverlayName,
    setOverlayData,
    triggerPageRefresh,
    openDialog,
    closeDialog,
    showSnackbar,
  } = useStateContext();
  const { socket } = useWebSocket();
  const task = overlayData.Task;

  const [members, setMembers] = useState([]);
  const [entityDetails, setEntityDetails] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Fetch members for assigned user lookup
  useEffect(() => {
    const fetchData = async () => {
      try {
        const membersRes = await api.get("/members");
        setMembers(membersRes.data);

        // Fetch entity details if entity exists
        if (task.EntityType && task.EntityID) {
          let endpoint = "";
          if (task.EntityType === "Client") {
            endpoint = `/clients/${task.EntityID}`;
          } else if (task.EntityType === "Linguist") {
            endpoint = `/linguists/${task.EntityID}`;
          } else if (task.EntityType === "Service") {
            // Use category-specific endpoints for services
            if (task.ServiceType === "Translation") {
              endpoint = `/services/translations/${task.EntityID}`;
            } else if (task.ServiceType === "Interpretation") {
              endpoint = `/services/interpretations/${task.EntityID}`;
            } else if (task.ServiceType === "Other Service") {
              endpoint = `/services/otherServices/${task.EntityID}`;
            }
          } else if (task.EntityType === "Invoice") {
            endpoint = `/invoices/${task.EntityID}`;
          }

          if (endpoint) {
            const entityRes = await api.get(endpoint);
            setEntityDetails(entityRes.data);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [task]);

  // Listen for task updates
  useEffect(() => {
    if (!socket || !task?.id) return;

    const handleTaskUpdate = (data) => {
      if (data.recordId === task.id) {
        // Update last update state to trigger snackbar
        setLastUpdate({
          changeAction: data.changeAction,
          updatedBy: data.updatedBy,
        });

        // Refetch task data
        triggerPageRefresh();
      }
    };

    socket.on("task:updated", handleTaskUpdate);
    return () => socket.off("task:updated", handleTaskUpdate);
  }, [socket, task?.id, triggerPageRefresh, theme]);

  // Show snackbar when update is received
  useEffect(() => {
    if (lastUpdate && showSnackbar) {
      const userName = lastUpdate.updatedBy?.userName || "Unknown User";
      const message = `Task ${lastUpdate.changeAction} by ${userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  const handleEdit = () => {
    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_EDIT,
      Task: task,
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
  };

  const handleDelete = () => {
    openDialog({
      theme: theme,
      title: "Delete Task",
      message: `Are you sure you want to delete the task "${task.Title}"? This action cannot be undone.`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        try {
          await api.delete(`/tasks/delete/${task.id}`);

          // Emit socket event for real-time updates
          if (socket) {
            socket.emit("task:saved", {
              modelType: "Task",
              recordId: task.id,
              entityType: task.EntityType,
              entityID: task.EntityID,
              serviceType: task.ServiceType,
              changeAction: "Deleted",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
            });
          }

          triggerPageRefresh("Task Deleted Successfully", theme);
          setOverlayName("");
        } catch (error) {
          console.error("Error deleting task:", error);
        }
        closeDialog();
      },
      secondaryOnClick: closeDialog,
    });
  };

  const handleOpenEntityProfile = () => {
    if (!task.EntityType || !task.EntityID) return;

    const { EntityType, EntityID, ServiceType } = task;
    let path = "";

    switch (EntityType) {
      case "Invoice":
        path = `/invoices/${EntityID}`;
        break;
      case "Client":
        path = `/clients/${EntityID}`;
        break;
      case "Linguist":
        path = `/linguists/${EntityID}`;
        break;
      case "Service":
        if (ServiceType === "Translation") {
          path = `/translations/${EntityID}`;
        } else if (ServiceType === "Interpretation") {
          path = `/interpretations/${EntityID}`;
        } else if (ServiceType === "Other Service") {
          path = `/otherServices/${EntityID}`;
        }
        break;
      default:
        console.error("Unknown EntityType:", EntityType);
        return;
    }

    if (path) {
      setOverlayName("");
      navigate(path, { state: { ComingFrom: "/tasks" } });
    }
  };

  const handleServiceTagClick = async (projectNumber) => {
    if (!projectNumber) return;

    try {
      const response = await api.get(
        `/services/tag-search?query=${encodeURIComponent(projectNumber)}&limit=10`
      );

      const options = Array.isArray(response.data) ? response.data : [];
      const matchingService =
        options.find(
          (service) => String(service?.ProjectNumber) === String(projectNumber)
        ) || options[0];

      const routePath = getServiceRoutePath(matchingService);
      if (!routePath) {
        showSnackbar?.(`No service found for #${projectNumber}`, theme, "warning");
        return;
      }

      setOverlayName("");
      navigate(routePath, { state: { ComingFrom: "/tasks" } });
    } catch (error) {
      console.error("Error opening tagged service:", error);
      showSnackbar?.(`Unable to open service #${projectNumber}`, theme, "error");
    }
  };

  const renderDescriptionWithServiceTags = (text) => {
    if (!text) return null;

    const content = String(text);
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = SERVICE_TAG_REGEX.exec(content)) !== null) {
      const fullMatch = match[0];
      const projectNumber = match[1];
      const matchStart = match.index;
      const matchEnd = match.index + fullMatch.length;

      if (matchStart > lastIndex) {
        parts.push(content.slice(lastIndex, matchStart));
      }

      parts.push(
        <Typography
          key={`${projectNumber}-${matchStart}`}
          component="span"
          onClick={() => handleServiceTagClick(projectNumber)}
          sx={{
            color: theme.palette.primary.main,
            cursor: "pointer",
            textDecoration: "underline",
            fontWeight: 600,
          }}
        >
          {fullMatch}
        </Typography>
      );

      lastIndex = matchEnd;
    }

    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts;
  };

  // Get priority color and icon
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Urgent":
        return theme.palette.error[400];
      case "High":
        return theme.palette.warning[400];
      case "Normal":
        return theme.palette.primary[400];
      case "Low":
        return theme.palette.grey[400];
      default:
        return theme.palette.grey[400];
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "Done":
        return theme.palette.success[400];
      case "In Progress":
        return theme.palette.info[400];
      case "Requires Action":
        return theme.palette.warning[400];
      case "Open":
        return theme.palette.primary[400];
      default:
        return theme.palette.grey[400];
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return "No due date set";
    return dayjs(date).format("MMMM DD, YYYY");
  };

  // Get due date color based on status
  const getDueDateColor = (date) => {
    if (!date) return theme.palette.primary.main;
    const today = dayjs().startOf("day");
    const dueDate = dayjs(date).startOf("day");

    if (dueDate.isSame(today)) {
      return theme.palette.error[400]; // Due today
    } else if (dueDate.isBefore(today)) {
      return theme.palette.warning[400]; // Overdue
    }
    return theme.palette.primary.main; // Future date
  };

  // Get assigned user name
  const getAssignedUserName = () => {
    if (!task.AssignedToUserID) return "Unassigned";
    const member = members.find((m) => m.id === task.AssignedToUserID);
    return member ? member.Name : "Unknown";
  };

  // Get entity label
  const getEntityLabel = () => {
    if (task.ServiceType) return task.ServiceType;
    if (task.EntityType) return task.EntityType;
    return "No entity linked";
  };

  // Get entity display name
  const getEntityDisplayName = () => {
    if (!entityDetails) return "Loading...";

    if (task.EntityType === "Client") {
      return (
        entityDetails.Account +
        (entityDetails.ShortName ? ` (${entityDetails.ShortName})` : "")
      );
    } else if (task.EntityType === "Linguist") {
      return entityDetails.Name;
    } else if (task.EntityType === "Service") {
      return `${entityDetails.ProjectNumber} - ${entityDetails.Details || "No details"}`;
    } else if (task.EntityType === "Invoice") {
      return `Invoice ${entityDetails.Number}`;
    }
    return "Unknown";
  };

  const footerActions = (
    <Box sx={{ display: "flex", gap: 1, width: "100%", justifyContent: "flex-end" }}>
      {isMobile ? (
        <>
          <Tooltip title="Delete">
            <IconButton
              color="primary"
              onClick={handleDelete}
              sx={{
                width: "40px",
                height: "40px",
                backgroundColor: theme.palette.background.default,
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.main + "20",
                },
              }}
            >
              <DeleteOutlineRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit Task">
            <IconButton
              color="primary"
              onClick={handleEdit}
              sx={{
                width: "40px",
                height: "40px",
                backgroundColor: theme.palette.primary.main,
                borderRadius: "12px",
                color: "white",
                "&:hover": {
                  backgroundColor:
                    theme.palette.primary.buttonHoverSelected,
                },
              }}
            >
              <EditRoundedIcon />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <>
          <Button
            color="error"
            variant="outlined"
            size="large"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={handleDelete}
            sx={{
              borderRadius: "12px",
            }}
          >
            Delete
          </Button>

          <Button
            color="primary"
            variant="contained"
            size="large"
            startIcon={<EditRoundedIcon />}
            onClick={handleEdit}
            sx={{
              borderRadius: "12px",
            }}
          >
            Edit Task
          </Button>
        </>
      )}
    </Box>
  );

  return (
    <Overlay
      id="task-profile"
      open={true}
      onClose={() => setOverlayName("")}
      title="Task Details"
      footer={footerActions}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          p: isMobile ? "1rem 1.3rem" : "1rem 2.3rem 1rem 2.5rem",
          width: "100%",
        }}
      >
        <Grid container spacing={3}>
          {/* Task Title Section */}
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                p: isMobile ? 2 : 2.5,
                borderRadius: "12px",
                bgcolor: theme.palette.background.default,
                border: `2px solid ${getStatusColor(task.Status)}`,
              }}
            >
              <Typography
                variant={isMobile ? "h4" : "h3"}
                fontWeight="bold"
                sx={{ mb: 1 }}
              >
                {task.Title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.primary.text2 }}
              >
                Created{" "}
                {dayjs(task.createdAt).format("MMMM DD, YYYY [at] h:mm A")}
              </Typography>
            </Box>
          </Grid>

            {/* Status and Priority Cards */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  bgcolor: theme.palette.background.default,
                  border: `2px solid ${getStatusColor(task.Status)}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    bgcolor: getStatusColor(task.Status),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircleOutlineRoundedIcon
                    sx={{ fontSize: 28, color: "white" }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.primary.text2, mb: 0.5 }}
                  >
                    Status
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {task.Status}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  bgcolor: theme.palette.background.default,
                  border: `2px solid ${getPriorityColor(task.Priority)}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    bgcolor: getPriorityColor(task.Priority),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FlagRoundedIcon sx={{ fontSize: 28, color: "white" }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.primary.text2, mb: 0.5 }}
                  >
                    Priority
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {task.Priority}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Due Date and Assigned To */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  bgcolor: theme.palette.background.default,
                  border:
                    task.DueDate &&
                    dayjs(task.DueDate).isBefore(dayjs().startOf("day"))
                      ? `2px solid ${getDueDateColor(task.DueDate)}`
                      : "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    bgcolor: getDueDateColor(task.DueDate),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CalendarTodayRoundedIcon
                    sx={{ fontSize: 24, color: "white" }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.primary.text2, mb: 0.5 }}
                  >
                    Due Date
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight="600"
                    sx={{
                      color:
                        task.DueDate &&
                        !dayjs(task.DueDate).isSame(dayjs(), "day") &&
                        dayjs(task.DueDate).isBefore(dayjs())
                          ? getDueDateColor(task.DueDate)
                          : "inherit",
                    }}
                  >
                    {formatDate(task.DueDate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  bgcolor: theme.palette.background.default,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    bgcolor: theme.palette.primary.main,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PersonRoundedIcon sx={{ fontSize: 28, color: "white" }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.primary.text2, mb: 0.5 }}
                  >
                    Assigned To
                  </Typography>
                  <Typography variant="h5" fontWeight="600">
                    {getAssignedUserName()}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Description Section */}
            {task.Description && (
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    bgcolor: theme.palette.background.default,
                  }}
                >
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ mb: 1.5, color: theme.palette.primary.text }}
                  >
                    Description
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.primary.text2,
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.8,
                    }}
                  >
                    {renderDescriptionWithServiceTags(task.Description)}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Related Entity Section */}
            {task.EntityType && task.EntityID && (
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    bgcolor: theme.palette.background.default,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "10px",
                          bgcolor: theme.palette.primary.main,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <LinkRoundedIcon
                          sx={{ fontSize: 24, color: "white" }}
                        />
                      </Box>
                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ color: theme.palette.primary.text }}
                      >
                        Related Entity
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleOpenEntityProfile}
                      sx={{
                        borderRadius: "10px",
                      }}
                    >
                      Open Profile
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Chip
                      label={getEntityLabel()}
                      size="small"
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: "white",
                        fontWeight: 600,
                        borderRadius: "8px",
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{ color: theme.palette.primary.text2 }}
                    >
                      {entityDetails
                        ? getEntityDisplayName()
                        : "Loading entity details..."}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Cancelled Status */}
            {task.IsCancelled && (
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    bgcolor: theme.palette.error.main + "20",
                    border: `2px solid ${theme.palette.error.main}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ color: theme.palette.error.main }}
                  >
                    ⚠ This task has been cancelled
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
      </Box>
    </Overlay>
  );
};

export default TaskProfile;
